import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'
import { type z } from 'zod'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { getApplicantDetailQuery } from '@/lib/queries/business/v1/applicant/queries'
import { editCaseMutation } from '@/lib/queries/business/v1/case/mutations'
import { editCategoryMutation } from '@/lib/queries/business/v1/case/path/mutations'
import { getCategoryListByApplicantQuery } from '@/lib/queries/business/v1/case/path/queries'
import { type ResCategory } from '@/lib/queries/business/v1/types'
import { getMembersQuery } from '@/lib/queries/business/v1/user/queries'

import { defaultCategories } from '../../../../../applicants/ui/case-dialogs/constants'
import type { ContentProps } from './content'
import { formSchema } from './validation'

export const useContent = ({ aCase, applicantId, onClose }: ContentProps) => {
  const queryClient = useQueryClient()

  const { data: dataMembers } = useQuery(getMembersQuery)
  const { data: dataListCategory } = useQuery(getCategoryListByApplicantQuery({ applicantId }))
  const { mutateAsync: createCategoryAsync, isPending: isPendingCreateCategory } = useMutation(editCategoryMutation)
  const { mutate: editCase, isPending: isPendingEditCase } = useMutation(editCaseMutation)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      assignees: dataMembers?.members?.map(({ id }) => String(id)),
      categoryId: String(aCase.path_id),
      categoryName: '',
      tags: aCase.tags?.join(', ') ?? '',
    },
  })
  const formErrors = form.formState.errors

  const categoryId = useWatch({ name: 'categoryId', control: form.control })
  const categoryName = useWatch({ name: 'categoryName', control: form.control })
  const assignees =
    dataMembers?.members?.map(({ id, name }) => ({
      label: `${name}${id === aCase.user_id ? ' (Owner)' : ''}`,
      value: String(id),
      disabled: id === aCase.user_id,
    })) ?? []
  const defaultAssignees = aCase.assignee?.map((userId) => String(userId)) || []

  const handleCategoryNameSave = useCallback(async () => {
    if (!categoryName.trim()) {
      return
    }

    const categories = await createCategoryAsync({
      applicantId: Number(applicantId),
      categoryName,
    })
    await queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.business.v1.case.path.index],
    })

    const newCategory = categories[categories.length - 1]

    form.setValue('categoryId', String(newCategory.id))
    form.resetField('categoryName')
    await form.trigger('categoryId')

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantId, categoryName])

  const handleSubmit = form.handleSubmit(async (data) => {
    const tagsSplit = data.tags?.split(/\s*,\s*/) ?? []
    const tagsTrimmed = tagsSplit.map((x) => x.trim())
    const tagsFiltered = tagsTrimmed.filter((x) => x.length > 0)
    const tags = [...new Set(tagsFiltered)]

    /*
     * if user selects a new category,
     * request for creating new category first then registering a new case
     */
    let newCategory: ResCategory | undefined
    if (data.categoryId.startsWith('_')) {
      const selectedCategory = defaultCategories.find((x) => x.id === data.categoryId)

      if (selectedCategory) {
        const categories = await createCategoryAsync({
          applicantId: Number(applicantId),
          categoryName: selectedCategory.label,
        })

        await queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.business.v1.case.path.index],
        })

        newCategory = categories[categories.length - 1]
      }
    }

    editCase(
      {
        assignee: data.assignees?.map((userId) => Number(userId)),
        caseId: aCase.case_id,
        categoryId: newCategory ? newCategory.id : Number(categoryId),
        tags,
      },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries(getApplicantDetailQuery(applicantId)),
            queryClient.invalidateQueries({
              queryKey: [API_ENDPOINTS.business.v1.case.path.index],
            }),
            queryClient.invalidateQueries({
              queryKey: [API_ENDPOINTS.business.v4.case.search.index],
            }),
          ])
          toast.success('Case has been edited.')
          form.reset()
          onClose()
        },
        onError: () => {
          toast.error('Failed to edit case.')
        },
      }
    )
  })

  return {
    dataListCategory,
    form,
    formErrors,
    categoryId,
    categoryName,
    assignees,
    defaultAssignees,
    isPendingCreateCategory,
    isPendingEditCase,
    handleCategoryNameSave,
    handleSubmit,
  }
}
